import Constants from "./CommonConstants";

export default ({ role = [], designation = "" }) => {
  const { additionalAdminRoles, BTTL } = Constants;
  return (
    role &&
    ((role.filter((role) => additionalAdminRoles.includes(role)).length > 0 &&
      designation == 29) ||
      (designation == 38 && role.includes(BTTL)) ||
      role.includes(Constants.housingAssuredAgentRole) ||
      role.includes(Constants.housingTMAgentRole))
  );
};

export const OWNER_BUSINESS_UNIT_NAME = Constants.BUSINESS_UNIT_NAME_OWNER;

export const getBusinessUnitDetails = (variousConfig = {}, businessUnit) => {
  const businessUnitMap = (variousConfig && variousConfig.businessUnit) || {};
  if (!businessUnit?.id) {
    return null;
  }
  return (
    Object.values(businessUnitMap).find((bu) => bu?.id === businessUnit.id) || null
  );
};

export const isOwnerBusinessUnit = (variousConfig = {}, businessUnit) => {
  const details = getBusinessUnitDetails(variousConfig, businessUnit);
  return (
    (details?.name || "").toLowerCase() === OWNER_BUSINESS_UNIT_NAME.toLowerCase()
  );
};

/**
 * Builds a normalized user object for LSQ sync by enriching basic user data
 * with fields from Redux state (agentBranchCityMap, userIdChildMap, etc.)
 * @param {Object} userData - Basic user object (from table row or form)
 * @param {Object} agentBranchCityMap - Map of userId to branch/city/region data
 * @param {Object} userIdChildMap - Map of userId to user details
 * @returns {Object} Enriched user object with all fields needed for parseUserDataForLsqSync
 */
export const buildUserObjectForLsqSync = (
  userData = {},
  agentBranchCityMap = {},
  userIdChildMap = {}
) => {
  const userId = userData.userId;
  if (!userId) {
    return userData;
  }

  // Get enriched data from agentBranchCityMap
  const branchCityData = agentBranchCityMap[userId] || {};
  const {
    salesBranchId,
    region: { id: regionId = "" } = {},
    tier,
    salesVertical: { id: salesVerticalId = "" } = {},
    businessUnit: { id: businessUnitId = "" } = {},
    salesChannel: { id: salesChannelId = "" } = {},
    roleType: { id: roleTypeId = "" } = {},
    departmentId,
    agentCityIds = [],
  } = branchCityData;

  // Get user details from userIdChildMap
  const userDetails = userIdChildMap[userId] || {};
  const {
    employeeCode,
    dateOfJoining,
    joiningDate,
    dateOfRelieving,
    name,
    email,
    manager,
    parentId,
    designation,
    designationId,
  } = userDetails;

  // Build enriched user object
  // Prefer values from userData, fallback to enriched data, then to userDetails
  const enrichedUser = {
    ...userData,
    // Basic fields - prefer userData, fallback to userDetails
    name: userData.name || name || "",
    email: userData.email || email || "",
    manager: userData.manager || manager || parentId || "",
    designation: userData.designation || designation || designationId || "",
    userId: userId,
    // City/Branch fields
    city: userData.city || salesBranchId || "",
    salesBranchId: userData.salesBranchId || salesBranchId || "",
    agentCityIds: userData.agentCityIds || agentCityIds || [],
    // Department
    department: userData.department || departmentId || "",
    // Region - ensure it's an object with id
    region: userData.region?.id
      ? { id: userData.region.id }
      : userData.region && typeof userData.region === "object"
      ? userData.region
      : regionId
      ? { id: regionId }
      : {},
    // Tier
    tier: userData.tier || tier || "",
    // Sales fields - ensure they're objects with id
    salesVertical: userData.salesVertical?.id
      ? { id: userData.salesVertical.id }
      : userData.salesVertical || (salesVerticalId ? { id: salesVerticalId } : {}),
    businessUnit: userData.businessUnit?.id
      ? { id: userData.businessUnit.id }
      : userData.businessUnit || (businessUnitId ? { id: businessUnitId } : {}),
    salesChannel: userData.salesChannel?.id
      ? { id: userData.salesChannel.id }
      : userData.salesChannel || (salesChannelId ? { id: salesChannelId } : {}),
    // RoleType - handle different possible formats
    roleType: userData.roleType?.id
      ? { id: userData.roleType.id }
      : userData.roleType ||
        (roleTypeId
          ? { id: roleTypeId }
          : userData.roleTypeId
          ? { id: userData.roleTypeId }
          : {}),
    roleTypeId: userData.roleTypeId || roleTypeId || "",
    // Date fields
    dateOfJoining: userData.dateOfJoining || dateOfJoining || joiningDate || null,
    dateOfRelieving: userData.dateOfRelieving || dateOfRelieving || null,
    // Employee code
    employeeCode: userData.employeeCode || employeeCode || "",
  };

  return enrichedUser;
};

export const parseUserDataForLsqSync = ({
  user,
  userId,
  variousConfig = {},
  masterDesignationsMap = {},
  makaanCitiesMap = {},
}) => {
  const formatDateToDDMMYY = (dateValue) => {
    if (!dateValue) return "";
    try {
      const date = new Date(dateValue);
      if (isNaN(date.getTime())) return "";
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = String(date.getFullYear()).slice(-2);
      return `${day}-${month}-${year}`;
    } catch (e) {
      return "";
    }
  };

  const getValueFromConfig = (configMap, id) => {
    if (!id || !configMap) return "";
    const item = configMap[id];
    return item?.name || "";
  };

  const getDesignationName = (designationId) => {
    return masterDesignationsMap[designationId] || "";
  };

  const getBranchName = (branchId) => {
    return makaanCitiesMap[branchId] || "";
  };

  const getCityGroupName = (tier) => {
    if (!tier) return "";
    const { empCityGroupMap = {} } = Constants;
    const cityGroup = empCityGroupMap[tier];
    return cityGroup?.name || "";
  };

  const payload = {
    agent_name: user.name || "",
    agent_id: userId || "",
    email_id: user.email || "",
    manager_id: user.manager || "",
    designation: getDesignationName(user.designation),
    branch: getBranchName(user.city || user.salesBranchId),
    region: getValueFromConfig(variousConfig.region, user.region?.id || user.region),
    city_group: getCityGroupName(user.tier),
    sales_channel: getValueFromConfig(
      variousConfig.salesChannel,
      user.salesChannel?.id || user.salesChannel
    ),
    business_unit: getValueFromConfig(
      variousConfig.businessUnit,
      user.businessUnit?.id || user.businessUnit
    ),
    sales_vertical: getValueFromConfig(
      variousConfig.salesVertical,
      user.salesVertical?.id || user.salesVertical
    ),
    role_type: getValueFromConfig(
      variousConfig.roleType,
      user.roleType?.id || user.roleTypeId || user.roleType
    ),
    department: getValueFromConfig(variousConfig.department, user.department),
    date_of_joining: formatDateToDDMMYY(user.dateOfJoining),
    employee_code: user.employeeCode || "",
  };

  return payload;
};
